import { useEffect } from 'react';
import { sendPresence } from '../services/data/ticket.data';
import { hasAccess } from '../services/helpers/auth-helper/auth-helper.service';
import { PERMISSIONS_LIST } from '../config/permissions.config';

export const HEARTBEAT_MS = 60_000;
export const ACTIVE_WINDOW_MS = 15 * 60_000;

/** Whether a heartbeat is due: the person used the admin in the last 15 minutes. */
export const isActive = (lastActivity: number, now: number): boolean =>
  now - lastActivity < ACTIVE_WINDOW_MS;

/**
 * Tells the helpdesk "this person is at the admin" once a minute (Trello #244). It drives
 * auto-assignment: tickets go to someone who is here, and an owner away 15 minutes has
 * theirs reassigned. Only while the person is actually using the admin (any click, key,
 * scroll or pointer move in the last 15 minutes): a tab left open overnight does not count.
 * Only for people on the helpdesk (ticket permissions).
 */
export function usePresenceHeartbeat(): void {
  useEffect(() => {
    if (!hasAccess([PERMISSIONS_LIST.ticketsFullAccess, PERMISSIONS_LIST.ticketsReadAccess])) {
      return undefined;
    }
    let lastActivity = Date.now();
    const mark = () => {
      lastActivity = Date.now();
    };
    const events = ['pointerdown', 'keydown', 'scroll', 'pointermove'] as const;
    events.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    const beat = () => {
      if (isActive(lastActivity, Date.now())) sendPresence();
    };
    beat();
    const timer = window.setInterval(beat, HEARTBEAT_MS);
    return () => {
      window.clearInterval(timer);
      events.forEach((e) => window.removeEventListener(e, mark));
    };
  }, []);
}
